import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createMarking as createMarkingService,
  updateMarking as updateMarkingService,
  deleteMarking as deleteMarkingService, 
  getMarking
} from "../../services/curriculum/marking.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { changedCurriculum, updateMarkingList, updateMarkingListMethod } from "../../store/actions/curriculum.actions";
import isEmpty from 'lodash/isEmpty';
import { useParams } from "react-router-dom";

const useMarking = () => {
  const { curriculumId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchMarking = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getMarking(curriculumId);
      dispatch(updateMarkingList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchMarking();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      value: 'required|numeric',
      primaryColor: 'required',
      secondaryColor: 'required'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createMarking = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createMarkingService(curriculumId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateMarkingListMethod('POST', response.data));
        dispatch(changedCurriculum());
        enqueueSnackbar('Marking created successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateMarking = async (markingId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateMarkingService(curriculumId, markingId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateMarkingListMethod('PUT', response.data));
        dispatch(changedCurriculum());
        enqueueSnackbar('Marking updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteMarking = async (markingId) => {
    try {
      const response = await deleteMarkingService(curriculumId, markingId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateMarkingListMethod('DELETE', markingId));
        dispatch(changedCurriculum());
        enqueueSnackbar('Marking deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createMarking,
    updateMarking,
    deleteMarking
  }
};

export default useMarking;